import { coerceArray } from '@angular/cdk/coercion';
import { inject, Injectable } from '@angular/core';
import { isNil } from 'lodash';
import { from, mergeMap, Observable, of, ReplaySubject, throwError } from 'rxjs';

import { AppConfig } from '@app/app.config';
import { environment } from 'environments/environment';
import { IntegrationServiceBase } from '@app/integrations/services/integration.service.base';
import { productAdIntelligencePath } from '@app/config';
import { AuthHelpers, AuthTokenData, RequestService, SemrushProduct } from '@shared/services';
import { WINDOW } from '@shared/services/window.providers';
import { CommonHelpers } from '@shared/helpers/common.helpers';
import { PACKAGE } from '../semrush.types';
import { PricesConfigResponse, SMSDK } from './semrush.service.types';
import { ViewportService } from './viewport.service';
import { catchError, switchMap } from 'rxjs/operators';
import { AllowNotificationsModalComponent } from '@app/integrations/semrush/components/allow-notifications-modal/allow-notifications-modal.component';
import { MatDialog } from '@angular/material/dialog';
import { LOCAL_STORAGE_KEY, StorageService } from '@app/core';
import { EntityItem } from '@main/models';

const EMAIL_SUBSCRIPTION_ERROR_MESSAGE = 'Email subscription cannot be suggested';

@Injectable({ providedIn: 'root' })
export class SemrushService implements IntegrationServiceBase {
    private readonly requestService = inject(RequestService);
    private readonly viewportService = inject(ViewportService);
    private readonly storageService = inject(StorageService);
    private readonly matDialog = inject(MatDialog);
    private readonly window = inject(WINDOW);
    private readonly config = inject(AppConfig);

    private readonly SDK$ = new ReplaySubject<SMSDK>(1);

    private sdkInjected = false;

    private tokenData: AuthTokenData;
    private activePackageIds: string[] = [];

    getUserIdentifier(): string {
        return this.tokenData.viewer_id;
    }

    get isMainProductTrialAvailable(): boolean {
        return !!this.tokenData?.is_main_product_trial_available;
    }

    get productTrialsAvailable(): string[] {
        return this.tokenData?.product_trials_available || [];
    }

    get activeProducts(): SemrushProduct[] {
        return this.tokenData?.active_products || [];
    }

    get hasEmailSubscription(): boolean {
        return this.tokenData?.email_subscription?.state === 'SUBSCRIBED';
    }

    requestEmailReports(): Observable<any> {
        return this.SDK$.pipe(switchMap((SM) => from(SM.client('requestEmailReports')))).pipe(
            catchError((err) => {
                if (err === EMAIL_SUBSCRIPTION_ERROR_MESSAGE) {
                    this.openNotificationPopup();
                }
                return throwError(() => err);
            }),
        );
    }

    getToken(): Observable<string> {
        if (!this.sdkInjected) {
            return throwError(() => new Error('SDK not loaded'));
        }

        return this.SDK$.pipe(mergeMap((SM) => from(SM.client('getAccessToken'))));
    }

    setToken(token: string): void {
        this.tokenData = AuthHelpers.decodeToken(token);
        this.activePackageIds = this.getPackagesIds();
    }

    getIntegrationRedirectPage(): Observable<string> {
        const url = this.hasActivePackages() ? this.tokenData.url : [productAdIntelligencePath, 'pricing'].join('/');

        return of(url);
    }

    getContactSupportLink(): string {
        return 'https://www.semrush.com/company/contacts/';
    }

    getPrices(): Observable<PricesConfigResponse> {
        return this.requestService.pureRequest('GET', '/assets/config/semrush-prices.json');
    }

    addPackages(names: PACKAGE | PACKAGE[]): void {
        const productIds = coerceArray(names).map((name) => this.getSemrushIdForPackage(name));

        this.SDK$.subscribe((SM) => {
            SM.client('requestInAppPurchase', productIds);
        });
    }

    hasActivePackages(): boolean {
        return this.getPackagesIds().length > 0;
    }

    getActivePackages(): PACKAGE[] {
        const configPackages = Object.keys(environment.semrushPackagesIds) as PACKAGE[];

        return configPackages.filter((packageName) => this.isPackageActive(packageName));
    }

    isPackageActive(packageName: PACKAGE): boolean {
        const packageId = this.getSemrushIdForPackage(packageName);

        return this.activePackageIds.includes(packageId);
    }

    isAllPackageActive(): boolean {
        return (
            this.isPackageActive(PACKAGE.ALL) ||
            (this.isPackageActive(PACKAGE.DISPLAY) && this.isPackageActive(PACKAGE.SOCIAL_VIDEO))
        );
    }

    isTrialAvailable(packageName: PACKAGE): boolean {
        const packageId = this.getSemrushIdForPackage(packageName);

        return this.productTrialsAvailable.includes(packageId);
    }

    isTrialExpired(packageName: PACKAGE): boolean {
        return !this.isTrialAvailable(packageName) && !this.isPackageActive(packageName);
    }

    loadSdk(): void {
        if (this.sdkInjected) {
            return;
        }

        this.sdkInjected = true;

        this.SDK$.subscribe((SDK) => {
            this.initHistoryChangeHandlers(SDK);

            if (this.config.appInfo.fullHeightLayout) {
                this.viewportService.setSDK(SDK);
                this.viewportService.init();
            }
        });

        (this.window as any).onSmSdkLoad = () => {
            const SDK = (this.window as any).SM as SMSDK;

            if (isNil(SDK)) {
                return;
            }

            SDK.init().then(() => {
                this.SDK$.next(SDK);
            });
        };

        CommonHelpers.injectScript(environment.semrushSdkPath);
    }

    private initHistoryChangeHandlers(SDK: SMSDK): void {
        const currentUrl = new URL(window.location.href);

        SDK.client('replaceUrl', currentUrl.pathname + currentUrl.search);

        CommonHelpers.decorateHistoryChangeHandlers((method, url) => SDK.client(method, url));
    }

    private getPackagesIds(): string[] {
        return this.activeProducts.map((activeProduct) => activeProduct.id);
    }

    private getSemrushIdForPackage(packageName: PACKAGE): string {
        return environment.semrushPackagesIds[packageName];
    }

    private openNotificationPopup() {
        this.matDialog.open(AllowNotificationsModalComponent, {
            width: '384px',
            position: {
                top: '155px',
                right: '50px',
            },
        });
    }

    saveNotificationData(entity: EntityItem, countryId: number) {
        this.storageService.set(LOCAL_STORAGE_KEY.SEMRUSH_NOTIFICATION_DATA, JSON.stringify({ entity, countryId }));
    }

    getNotificationData(): string | undefined {
        return this.storageService.get(LOCAL_STORAGE_KEY.SEMRUSH_NOTIFICATION_DATA);
    }

    clearNotificationData() {
        this.storageService.remove(LOCAL_STORAGE_KEY.SEMRUSH_NOTIFICATION_DATA);
    }
}
